/**
 * Day and round flow: level switch, day picker (playtest builds only), progress in
 * localStorage, verdicts, summary and share text, and silent Knowledge Map collection.
 */
import type { LatLng } from "./geo";
import { h, $, toNodes, reducedMotion } from "../ui/dom";
import { MapView, type Line, type Pin } from "../map/MapView";
import { routeLayers } from "../rounds/trail";
import { dayRoutes } from "./dayRoutes";
import { cardText } from "./card";
import { renderCard } from "../ui/card";
import { ROUNDS } from "../rounds";
import type { Child, Host, RoundState } from "../rounds/types";
import { derive, type Playable } from "./derive";
import { clampRound, distanceScore, HINT_CAPS, maxOf, per1000, PIN_MAX, PIN_RINGS_KM, roundBand, WHO_POINTS, whoPlaceOf } from "./scoring";
import { roundSeed } from "./random";
import { dayTotal, maxOfResult, namedAt, shareText, TYPE_ICON, type RoundResult } from "./share";
import { credit } from "./knowledge";
import { recordPlayed, getPlayed, streakOf } from "./streak";
import { byTieBreak, challengeUrl, decodeChallenge, isOldChallenge, outcome, type Challenge } from "./challenge";
import { decodeBackup, encodeBackup, exportState, isOldBackup, mergeState, writeState } from "./backup";
import { dropOldKeys, store } from "./storage";
import { setVia, track, trackActive } from "./track";
import { drafts, fetchDay, index, localDate, names, playableDays, setFor } from "../puzzles/load";
import type { Day, DayRef, Level, Place } from "../puzzles/types";
import { msUntilDay } from "./clock";
import { currentResults, dailyRef, dayIdOf, modeOf, nextDayDate, nextUnplayed, streakDate, vaultGate, vaultRefs, vaultRows, type Gate, type Mode } from "./vault";
import { t } from "../strings";
import { n } from "../strings/en";

/** Origin + base path, without a trailing slash: https://whosteps.com, or …/playtest in the playtest build. */
const appUrl = (): string => `${location.origin}${import.meta.env.BASE_URL}`.replace(/\/$/, "");

/** Share icon (box with an arrow out of it), drawn in currentColor. */
/** Where the share button can send the result: the three apps friends and family actually use. */
const SHARE_APPS = ["whatsapp", "telegram"] as const;

function shareIcon(): SVGSVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  for (const [k, v] of [["viewBox", "0 0 24 24"], ["width", "22"], ["height", "22"], ["fill", "none"], ["stroke", "currentColor"], ["stroke-width", "2"], ["stroke-linecap", "round"], ["stroke-linejoin", "round"], ["aria-hidden", "true"]]) svg.setAttribute(k!, v!);
  for (const d of ["M12 3v12", "M7.5 7.5 12 3l4.5 4.5", "M8 11H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-2"]) {
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", d);
    svg.append(path);
  }
  return svg;
}

/** Vault icon (an archive box), drawn in currentColor. */
function vaultIcon(): SVGSVGElement {
  const NS = "http://www.w3.org/2000/svg";
  const svg = document.createElementNS(NS, "svg");
  for (const [k, v] of [["viewBox", "0 0 24 24"], ["width", "24"], ["height", "24"], ["fill", "none"], ["stroke", "currentColor"], ["stroke-width", "1.8"], ["stroke-linecap", "round"], ["stroke-linejoin", "round"], ["aria-hidden", "true"], ["class", "ic"]]) svg.setAttribute(k!, v!);
  for (const d of ["M3 4.5h18v4H3z", "M4.5 8.5V19a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1V8.5", "M10 12.5h4"]) {
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", d);
    svg.append(path);
  }
  return svg;
}

/** Levels offered: Family (easy) and Classic (medium). Hard was merged into Classic on 2026-09-29 (DECISIONS). */
const LEVELS: Level[] = ["easy", "medium"];
/** Old "hard" choices, links and saved settings open Classic. */
const offered = (l: Level): Level => (l === "hard" ? "medium" : l);

interface Saved {
  results: RoundResult[];
  /** How the day was played (absent in saves from before the Vault = daily). */
  mode?: Mode;
  /** Local date of the last round played. */
  playedOn?: string;
}

/** What was on screen before the panel was rebuilt, to restore focus and scroll after. */
interface Snap {
  inPanel: boolean;
  key: string | null;
  id: string | null;
  act: string | null;
  caret: [number, number] | null;
  verdict: boolean;
  submit: boolean;
}

export class Game {
  private readonly map: MapView;
  private level: Level;
  private days: Day[] = [];
  private dayIdx = 0;
  private saved: Saved = { results: [] };
  private round = 0;
  private p!: Playable;
  private st!: RoundState & { verdict?: { score: number; reveal: string } };
  private toastTimer = 0;
  /** Bumped to cancel a running route animation. */
  private revealRun = 0;
  /** A challenge link was just opened (show "other day" note if it doesn't match). */
  private freshChallenge = false;
  /** Bumped per loadLevel call so a slow fetch can't overwrite a newer level choice. */
  private loadSeq = 0;
  /** Days started this visit (level:day), so switching back and forth sends one "start". */
  private readonly started = new Set<string>();
  /** First-visit "How to play" card (reopened from the header button). */
  /** The full How to play card (header button). A first visit gets the short card over the map instead. */
  private introOpen = false;
  /** First visit: the three-line card over the top of the map (DECISIONS 2026-10-03 "A shorter first visit"). */
  private quickOpen = !store.get("intro");
  /** Announcements queued within one tick go out together. */
  private announcing: string[] = [];
  /** What the panel shows: a day (rounds or its summary) or the Vault's list of past days. */
  private view: "play" | "vault" = "play";
  /** Replaying a finished past day: nothing is saved, credited or tracked. */
  private practice = false;
  /** The "Next puzzle in …" countdown on today's finished puzzle. */
  private hubTimer = 0;
  /** Playtest day picker open (it starts folded to one line). */
  private daysOpen = false;
  /** Past days list filter (a per-device convenience). */
  private unplayedOnly = store.get<boolean>("vaultUnplayed") === true;

  constructor() {
    this.labelShell();
    this.map = new MapView($("mapwrap"), $("map") as HTMLCanvasElement);
    $("zin").onclick = () => this.map.zoomBy(2);
    $("zout").onclick = () => this.map.zoomBy(0.5);
    $("zfit").onclick = () => this.map.homeView?.(true);
    $("howto").onclick = () => this.openIntro();
    this.quickCard();
    $("vault").onclick = () => this.vaultButton();
    const stored = store.get<Level>("level");
    this.level = stored && LEVELS.includes(offered(stored)) ? offered(stored) : "medium";
    if (drafts) $("banner").hidden = false;

    dropOldKeys(); // one-off after the restart (DECISIONS 2026-10-03 "Restart")
    const restore = /^#restore=([A-Za-z0-9_-]+)$/.exec(location.hash);
    if (restore) {
      this.cleanUrl(false);
      setVia("restore");
      const old = isOldBackup(restore[1]!);
      void (old ? Promise.resolve(null) : decodeBackup(restore[1]!)).then((s) => {
        track({ e: "restore", ok: s ? 1 : 0 });
        if (s) writeState(mergeState(exportState(), s));
        this.start();
        this.toast(s ? t.restore.done : old ? t.restore.old : t.restore.failed);
      });
    } else this.start();
  }

  /** Static shell text lives in en.ts, not index.html. */
  private labelShell(): void {
    $("banner").textContent = t.ui.draftsBanner;
    $("modes").setAttribute("aria-label", t.ui.difficulty);
    $("days").setAttribute("aria-label", t.ui.puzzleNumber);
    for (const [id, label] of [["zin", t.ui.zoomIn], ["zout", t.ui.zoomOut], ["zfit", t.ui.resetView]] as const) {
      $(id).setAttribute("aria-label", label);
      $(id).title = label;
    }
    $("howto").replaceChildren(h("span", { class: "q", "aria-hidden": "true" }, "?"), h("span", { class: "lbl" }, t.ui.howTo));
    $("howto").title = t.ui.howTo;
    $("vault").replaceChildren(vaultIcon(), h("span", { class: "lbl" }, t.vault.button));
  }

  private start(): void {
    // Challenge link: ?c=m4-1512-3 — play that level; the friend's score shows after finishing.
    const code = new URLSearchParams(location.search).get("c");
    const c = decodeChallenge(code);
    if (!c && isOldChallenge(code)) {
      // a link from before the restart: its puzzle number means a different puzzle now
      this.cleanUrl(true);
      this.toast(t.challenge.old);
    }
    if (c) {
      store.set("challenge", c);
      this.level = offered(c.level);
      store.set("level", this.level);
      this.freshChallenge = true;
      this.cleanUrl(true);
      setVia("challenge");
      track({ e: "challenge", l: c.level, d: c.day });
    }
    // Playtest deep link: #<level>/<day number>
    const m = /^#(easy|medium|hard)\/(\d+)$/.exec(location.hash);
    if (drafts && m) this.level = offered(m[1] as Level);
    this.loadLevel(m && drafts ? Number(m[2]) : c ? c.day : null);
    if (drafts) window.addEventListener("hashchange", () => this.followHash());
  }

  /** Drop ?c= (keepHash) or the #restore= fragment from the address bar so a reload doesn't repeat it. */
  private cleanUrl(keepHash: boolean): void {
    try {
      history.replaceState(null, "", location.pathname + (keepHash ? location.hash : ""));
    } catch {
      /* ignore */
    }
  }

  private challenge(): Challenge | null {
    return store.get<Challenge>("challenge");
  }

  /** The stored challenge, if it's for the day and level on screen. */
  private matchingChallenge(): Challenge | null {
    const c = this.challenge();
    return c && c.level === this.level && c.day === this.day.number ? c : null;
  }

  /** Playtest builds: typing #hard/4 into the address bar switches level and day. */
  private followHash(): void {
    const m = /^#(easy|medium|hard)\/(\d+)$/.exec(location.hash);
    if (!m) return;
    const level = offered(m[1] as Level);
    const num = Number(m[2]);
    if (level === this.level && this.days[this.dayIdx]?.number === num) return;
    this.level = level;
    this.loadLevel(num);
  }

  // ---------- state ----------
  private get day(): Day {
    return this.days[this.dayIdx]!;
  }
  private key(d = this.day): string {
    return `progress:${this.level}:${d.id}`;
  }
  /** Most points today's three rounds can give (depends on the round types and item counts). */
  private dayMax(): number {
    return this.day.rounds.reduce((a, r, i) => a + maxOf(derive(r, this.level, roundSeed(this.day.number - 1, i))), 0);
  }

  private total(): number {
    return this.saved.results.reduce((a, r) => a + r.score, 0);
  }

  // ---------- today's puzzle and the Vault (src/game/vault.ts) ----------
  private savedOf(level: Level, id: string): Saved | null {
    return store.get<Saved>(`progress:${level}:${id}`);
  }
  private isDone(level: Level, id: string): boolean {
    const s = this.savedOf(level, id);
    return !!s && s.results.length >= 3 && s.results.every(Boolean) && currentResults(s.results);
  }
  /** Today's puzzle at a level (from the bundled date index; no content). */
  private dailyOf(level = this.level): DayRef | null {
    return dailyRef(index, setFor(level), localDate());
  }
  private dailyDone(level = this.level): boolean {
    const d = this.dailyOf(level);
    return !!d && this.isDone(level, dayIdOf(d));
  }
  private pastRefs(level = this.level): DayRef[] {
    return vaultRefs(index, setFor(level), localDate());
  }
  private gate(level = this.level): Gate {
    return vaultGate(this.pastRefs(level).length, !!this.dailyOf(level), this.dailyDone(level));
  }
  private mode(d = this.day): Mode {
    return modeOf(d.date, this.dailyOf()?.date ?? null);
  }
  private isToday(d = this.day): boolean {
    const r = this.dailyOf();
    return !!r && d.date === r.date;
  }

  /** Load the level's playable days, then open `dayNumber` (or today's), or the Vault list with `toVault`. */
  private loadLevel(dayNumber: number | null, toVault = false): void {
    const seq = ++this.loadSeq;
    const slow = drafts ? 0 : window.setTimeout(() => seq === this.loadSeq && this.loadingPanel(), 250);
    playableDays(setFor(this.level)).then(
      (days) => {
        clearTimeout(slow);
        if (seq === this.loadSeq) this.showDays(days, dayNumber, toVault);
      },
      () => {
        clearTimeout(slow);
        if (seq === this.loadSeq) this.loadFailed(dayNumber);
      },
    );
  }

  private showDays(days: Day[], dayNumber: number | null, toVault = false): void {
    this.days = days;
    if (!this.days.length) return this.comingSoon();
    trackActive(localDate());
    if (toVault) return this.openVault(null);
    const unfinished = (d: Day) => !this.isDone(this.level, d.id);
    let idx = dayNumber != null ? this.days.findIndex((d) => d.number === dayNumber) : -1;
    const challenged = this.freshChallenge ? this.challenge() : null;
    if (challenged && challenged.day === dayNumber) {
      // A challenge link to a past day opens that day, even before today's puzzle (it's an invitation).
      const past = this.pastRefs().find((r) => r.number === dayNumber);
      if (past) {
        track({ e: "vault_open", l: this.level, f: "challenge" });
        if (idx < 0) return this.openPast(past);
      }
    }
    if (idx < 0) {
      const today = this.dailyOf();
      const j = today ? this.days.findIndex((d) => d.number === today.number) : -1;
      if (j >= 0 && unfinished(this.days[j]!)) idx = j;
    }
    if (idx < 0) idx = this.days.findIndex(unfinished);
    this.loadDay(idx < 0 ? 0 : idx);
  }

  /** Open a past day: from the list already loaded, else fetch its file. */
  private openPast(ref: DayRef): void {
    const have = this.days.findIndex((d) => d.set === ref.set && d.number === ref.number);
    if (have >= 0) return this.loadDay(have);
    const seq = ++this.loadSeq;
    const slow = window.setTimeout(() => seq === this.loadSeq && this.loadingPanel(t.ui.loadingDay), 250);
    fetchDay(ref).then(
      (day) => {
        clearTimeout(slow);
        if (seq !== this.loadSeq) return;
        this.days = [...this.days, day];
        this.loadDay(this.days.length - 1);
      },
      () => {
        clearTimeout(slow);
        if (seq !== this.loadSeq) return;
        this.toast(t.vault.loadFailed);
        this.openVault(null);
      },
    );
  }

  private loadDay(i: number): void {
    this.dayIdx = i;
    this.view = "play";
    this.practice = false;
    this.stopHub();
    // Results saved under older rules (another scale, or the round types before 30 Sep): start that day afresh.
    const saved = store.get<Saved>(this.key());
    this.saved = saved && currentResults(saved.results) ? saved : { results: [] };
    this.round = Math.min(this.saved.results.length, 3);
    if (drafts) {
      try {
        history.replaceState(null, "", `#${this.level}/${this.day.number}`);
      } catch {
        /* ignore */
      }
    }
    this.renderHead();
    if (this.round >= 3) this.showSummary();
    else this.startRound();
  }

  // ---------- header ----------
  private renderHead(): void {
    $("modes").hidden = false;
    $("modes").replaceChildren(
      ...LEVELS.map((k) =>
        h(
          "button",
          {
            type: "button",
            "aria-pressed": String(this.level === k),
            "data-level": k,
            onclick: () => {
              if (this.level === k) return;
              this.freshChallenge = false;
              // In the Vault, switching level shows that level's past days (if its Vault is open).
              const toVault = this.view === "vault" && this.gate(k) === "open";
              this.level = k;
              store.set("level", k);
              this.loadLevel(null, toVault);
            },
          },
          t.levels[k].label,
          h("small", null, t.levels[k].sub),
        ),
      ),
    );
    // Playtest day picker: one line ("Puzzle #3 ▾") that opens the grid, so the question stays on screen (Egor, 30 Sep).
    const daysEl = $("days");
    daysEl.hidden = !drafts || this.days.length < 2;
    const current = this.view === "play" ? this.days[this.dayIdx] : undefined;
    const toggle = h(
      "button",
      {
        class: "days-toggle",
        type: "button",
        "aria-expanded": String(this.daysOpen),
        "data-act": "days-toggle",
        "data-key": "days-toggle",
        onclick: () => {
          this.daysOpen = !this.daysOpen;
          this.renderHead();
        },
      },
      current ? t.ui.dayPicker(current.number, this.days.length) : t.ui.dayPickerNone(this.days.length),
      h("span", { class: "chev", "aria-hidden": "true" }, "▾"),
    );
    const grid = this.daysOpen
      ? h(
          "div",
          { class: "days-grid" },
          this.days.map((d, i) =>
            h(
              "button",
              {
                type: "button",
                "aria-current": String(this.view === "play" && i === this.dayIdx),
                class: (store.get<Saved>(this.key(d))?.results.length ?? 0) >= 3 ? "done" : "",
                "data-key": `day-${d.number}`,
                onclick: () => {
                  this.freshChallenge = false;
                  this.daysOpen = false;
                  this.loadDay(i);
                },
              },
              `#${d.number}`,
            ),
          ),
        )
      : null;
    daysEl.replaceChildren(...toNodes([toggle, grid]));
    $("tot").textContent = this.view === "vault" || !this.days.length ? "" : this.saved.results.length ? t.ui.total(this.total(), this.dayMax()) : t.ui.totalIdle(this.dayMax());
    this.renderVaultButton();
  }

  /** Header Vault button: hidden until there are enough past days, locked until today's puzzle is done. */
  private renderVaultButton(): void {
    const g = this.gate();
    const vb = $("vault");
    vb.hidden = g === "hidden";
    vb.setAttribute("aria-disabled", String(g === "locked"));
    vb.classList.toggle("on", this.view === "vault");
    vb.title = g === "locked" ? t.vault.locked : t.vault.button;
  }

  private vaultButton(): void {
    const g = this.gate();
    if (g === "locked") return this.toast(t.vault.locked);
    if (g !== "open") return;
    if (this.view === "vault") return document.querySelector<HTMLElement>("#panel h2.rt")?.focus();
    this.openVault("header");
  }

  // ---------- rounds ----------
  private host(): Host {
    return {
      map: this.map,
      names,
      seed: roundSeed(this.day.number - 1, this.round),
      frame: (p, ...kids) => this.frame(p, ...kids),
      finish: (score, extra, reveal, credits) => this.finish(score, extra, reveal, credits),
      verdict: () => this.verdictBox(),
      draw: () => this.draw(),
      animate: (ms, step) => this.animate(ms, step),
      toast: (msg) => this.toast(msg),
      announce: (msg) => this.announce(msg),
      level: this.level,
      firstPin: (ll) => this.zoomNudge(ll),
    };
  }

  private startRound(): void {
    const startKey = `${this.level}:${this.day.id}`;
    if (!this.practice && this.round === 0 && !this.saved.results.length && !this.started.has(startKey)) {
      this.started.add(startKey);
      track({ e: "start", l: this.level, d: this.day.number, md: this.mode() });
    }
    const source = this.day.rounds[this.round]!;
    this.p = derive(source, this.level, roundSeed(this.day.number - 1, this.round));
    const mod = ROUNDS[this.p.type];
    const host = this.host();
    this.st = mod.init(this.p, host);
    this.revealRun++;
    this.map.onMapClick = null;
    this.map.onPinClick = null;
    this.map.showCallout(null);
    this.draw();
    this.legend(false);
    this.map.homeView = (anim) => mod.view(this.host(), this.st, this.p, anim ? 700 : 0);
    this.map.homeView(true);
  }

  private draw(): void {
    ROUNDS[this.p.type].render(this.host(), this.st, this.p);
  }

  /** Frame-by-frame map animation for the current round; a new round (revealRun) stops it. */
  private animate(ms: number, step: (t: number) => void): void {
    if (ms <= 0 || reducedMotion()) return step(1);
    const run = this.revealRun;
    let t0: number | null = null; // frame timestamps only, so a faked Date (tests) can't stall it
    const tick = (now: number) => {
      if (run !== this.revealRun) return;
      t0 ??= now;
      const t = Math.min(1, Math.max(0, (now - t0) / ms));
      step(t);
      if (t < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  private progressBar(): HTMLElement {
    return h(
      "div",
      { class: "rounds", "aria-hidden": "true" },
      [0, 1, 2].map((i) => {
        const r = this.saved.results[i];
        return h("span", { class: r ? `s${roundBand(r.type, r.score, maxOfResult(r))}` : i === this.round ? "cur" : "" });
      }),
    );
  }

  private frame(p: Playable, ...kids: Child[]): void {
    const panel = $("panel");
    const before = this.snapshot(panel);
    const rk = `${this.level}|${this.day.id}|${this.round}|${this.practice ? "p" : ""}`;
    const fresh = panel.dataset.rk !== rk;
    panel.dataset.rk = rk;
    panel.dataset.roundType = p.type;
    const title = h("h2", { class: "rt", tabindex: "-1" }, p.title);
    panel.replaceChildren(
      ...toNodes([
        this.introOpen ? this.introCard() : null,
        this.progressBar(),
        h("div", { class: "rhead" }, h("div", { class: "kicker" }, h("span", null, this.roundKicker())), title),
        this.round === 0 && !this.practice ? this.challengeNote() : null,
        ...kids,
      ]),
    );
    this.afterRender(panel, before, fresh, title);
  }

  private roundKicker(): string {
    const r = this.round + 1;
    if (this.practice) return t.vault.practiceRound(r);
    return this.mode() === "vault" ? t.vault.roundKicker(r, this.day.number) : t.ui.roundOf(r, t.levels[this.level].label);
  }

  private snapshot(panel: HTMLElement): Snap {
    const a = document.activeElement as HTMLElement | null;
    const inPanel = !!a && a !== panel && panel.contains(a);
    const input = inPanel && a instanceof HTMLInputElement ? a : null;
    return {
      inPanel,
      key: inPanel ? (a.dataset.key ?? null) : null,
      id: inPanel && a.id ? a.id : null,
      act: inPanel ? (a.dataset.act ?? null) : null,
      caret: input && input.selectionStart != null ? [input.selectionStart, input.selectionEnd ?? input.selectionStart] : null,
      verdict: !!panel.querySelector(".verdict"),
      submit: !!panel.querySelector('[data-act="submit"]:not(:disabled)'),
    };
  }

  /**
   * After a rebuild: a new round (or the summary) starts at the top with its title focused; a new
   * verdict is focused, announced and scrolled into view; otherwise focus goes back to the same
   * control (data-key, then id, then data-act) and a newly enabled submit button is brought into view.
   */
  private afterRender(panel: HTMLElement, before: Snap, fresh: boolean, title: HTMLElement): void {
    const behavior: ScrollBehavior = reducedMotion() ? "auto" : "smooth";
    if (fresh) {
      panel.scrollTop = 0;
      if (before.inPanel) {
        this.panelToTop(panel);
        title.focus({ preventScroll: true });
      }
      return;
    }
    const v = panel.querySelector<HTMLElement>(".verdict");
    if (v && !before.verdict) {
      v.focus({ preventScroll: true });
      v.scrollIntoView({ block: "nearest", behavior });
      const sv = this.st.verdict;
      if (sv) {
        const word = this.verdictWord(sv.score);
        const said = !sv.reveal || sv.reveal.toLowerCase().startsWith(word.toLowerCase());
        this.announce(t.ui.verdictAnnounce(said ? "" : word, sv.reveal || word, sv.score));
      }
      return;
    }
    if (before.inPanel && !panel.contains(document.activeElement)) {
      const usable = (el: Element | null): el is HTMLElement => el instanceof HTMLElement && !el.matches(":disabled");
      const esc = (x: string) => CSS.escape(x);
      const tries = [
        before.key ? `[data-key="${esc(before.key)}"]` : null,
        before.id ? `#${esc(before.id)}` : null,
        before.act ? `[data-act="${esc(before.act)}"]:not(:disabled)` : null,
      ];
      let el: HTMLElement | null = null;
      for (const sel of tries) {
        const found = sel ? panel.querySelector(sel) : null;
        if (usable(found)) {
          el = found;
          break;
        }
      }
      (el ?? title).focus({ preventScroll: true });
      if (el instanceof HTMLInputElement && before.caret) el.setSelectionRange(...before.caret);
    }
    const sub = panel.querySelector<HTMLElement>('[data-act="submit"]:not(:disabled)');
    if (sub && !before.submit) sub.scrollIntoView({ block: "nearest", behavior });
  }

  /** Phones: the page scrolls under a sticky map. Bring the panel's top back just below the map. */
  private panelToTop(panel: HTMLElement): void {
    const wrap = $("mapwrap");
    if (getComputedStyle(wrap).position !== "sticky") return;
    const gap = panel.getBoundingClientRect().top - wrap.getBoundingClientRect().bottom;
    if (gap < 0) window.scrollBy({ top: gap - 4, behavior: "auto" });
  }

  /** Polite screen-reader message in the dedicated #announce region (cleared first so repeats are read). */
  private announce(msg: string): void {
    const el = $("announce");
    if (!this.announcing.length) {
      el.textContent = "";
      window.setTimeout(() => {
        el.textContent = this.announcing.join(" ");
        this.announcing = [];
      }, 80);
    }
    this.announcing.push(msg);
  }

  // ---------- how to play ----------
  private introCard(): HTMLElement {
    return h(
      "section",
      { class: "intro", id: "intro", "aria-labelledby": "introTitle" },
      h("h2", { id: "introTitle", tabindex: "-1" }, t.intro.title),
      h("p", { class: "lead" }, t.intro.what),
      h(
        "ul",
        null,
        h("li", null, t.intro.rounds(PIN_MAX)),
        h("li", { "data-act": "intro-rule" }, t.intro.rule(PIN_RINGS_KM.map((km) => [km, distanceScore(km)] as const), HINT_CAPS, WHO_POINTS)),
        h("li", { "data-act": "intro-signature" }, t.intro.signature),
        h("li", null, t.intro.daily),
        h("li", null, t.intro.who),
        h("li", null, t.intro.levels),
      ),
      h("p", { class: "hint" }, `${t.intro.privacy} `, h("a", { href: "/privacy", "data-act": "privacy" }, t.ui.privacy)),
      h("div", { class: "row" }, h("button", { class: "btn", type: "button", "data-act": "intro-close", onclick: () => this.closeIntro(true) }, store.get("intro") ? t.intro.closeAgain : t.intro.close)),
    );
  }

  private openIntro(): void {
    const panel = $("panel");
    this.introOpen = true;
    let card = document.getElementById("intro");
    if (!card) {
      card = this.introCard();
      panel.prepend(card);
    }
    panel.scrollTop = 0;
    card.scrollIntoView({ block: "nearest", behavior: reducedMotion() ? "auto" : "smooth" });
    card.querySelector<HTMLElement>("h2")?.focus({ preventScroll: true });
  }

  private closeIntro(moveFocus: boolean): void {
    this.introOpen = false;
    store.set("intro", 1);
    const card = document.getElementById("intro");
    const hadFocus = !!card && card.contains(document.activeElement);
    card?.remove();
    if (moveFocus && hadFocus) ($("panel").querySelector<HTMLElement>("h2.rt, #revealName") ?? $("howto")).focus();
  }

  /** First visit: three short lines over the top of the map, so the first clue stays on screen. */
  private quickCard(): void {
    if (!this.quickOpen || document.getElementById("quick")) return;
    $("mapwrap").append(
      h(
        "section",
        { class: "quick", id: "quick", "aria-labelledby": "quickTitle" },
        h("h2", { id: "quickTitle", class: "sr-only" }, t.intro.quickTitle),
        h("ol", null, t.intro.quick.map((line) => h("li", null, line))),
        h(
          "div",
          { class: "row" },
          h("button", { class: "btn", type: "button", "data-act": "intro-close", "data-key": "quick-close", onclick: () => this.closeQuick(true) }, t.intro.gotIt),
          h(
            "button",
            {
              class: "btn ghost",
              type: "button",
              "data-act": "intro-more",
              onclick: () => {
                this.closeQuick(false);
                this.openIntro();
              },
            },
            t.intro.title,
          ),
        ),
      ),
    );
  }

  private closeQuick(moveFocus: boolean): void {
    this.quickOpen = false;
    store.set("intro", 1);
    const card = document.getElementById("quick");
    const hadFocus = !!card && card.contains(document.activeElement);
    card?.remove();
    if (moveFocus && hadFocus) $("panel").querySelector<HTMLElement>("h2.rt, #revealName")?.focus();
  }

  /**
   * The player's first pin ever in a Classic Pin it round: if the map is still at world scale, one note by the
   * pin that zooming in pays (shown once, never in Family). The ring for 20 points comes from scoring.ts.
   */
  private zoomNudge(ll: LatLng): void {
    if (this.level === "easy" || this.practice || store.get("zoomNudge")) return;
    store.set("zoomNudge", 1);
    if (!this.map.worldScale) return;
    this.map.showCallout({ lat: ll.lat, lng: ll.lng, title: t.pin.zoomNudge(PIN_RINGS_KM[0]!, distanceScore(PIN_RINGS_KM[0]!)) });
    this.announce(t.pin.zoomNudge(PIN_RINGS_KM[0]!, distanceScore(PIN_RINGS_KM[0]!)));
  }

  /**
   * The score screen's map (DECISIONS 2026-10-03 "The map of your day"): the day's three routes in their own colours,
   * the player's two pins as hollow markers, fitted to all of it; tap a stop for its note. Static (no animation).
   */
  private dayMap(results: readonly RoundResult[]): void {
    const map = this.map;
    const C = map.colors;
    const routes = dayRoutes(this.day);
    const groups = [
      { stops: routes.pin, color: C["route-pin"], idBase: 1000, tag: "route-pin" },
      { stops: routes.next, color: C["route-next"], idBase: 2000, tag: "route-next" },
      { stops: routes.who, color: C["route-who"], idBase: 3000, tag: "route-who" },
    ];
    const pins: Pin[] = [];
    const lines: Line[] = [];
    const places: Record<number, Place> = {};
    for (const g of groups) {
      const r = routeLayers(g);
      pins.push(...r.pins);
      lines.push(...r.lines);
      g.stops.forEach((p, i) => (places[g.idBase + i] = p));
    }
    // The player's pins (Pin it, Next stop), hollow, in their round's colour; older saves have no position.
    results.slice(0, 2).forEach((r, i) => {
      if (r?.at) pins.push({ lat: r.at[0], lng: r.at[1], kind: "guess", color: groups[i]!.color, z: 3, tag: `guess-${groups[i]!.tag}` });
    });
    map.set({ pins, lines, cities: true });
    map.onPinClick = (id) => {
      const p = places[id];
      if (p) map.showCallout({ lat: p.lat, lng: p.lng, title: p.name, sub: p.when ?? null, body: p.note ?? null });
    };
    const all = [...groups.flatMap((g) => g.stops), ...pins];
    this.legend(true);
    // keep the stops clear of the legend in the bottom-left corner
    const padBottom = (document.getElementById("legend")?.offsetHeight ?? 0) + 12;
    map.homeView = (anim) => map.fitPoints(all, { ms: anim && !reducedMotion() ? 700 : 0, maxWorldPx: 60000, padBottom });
    map.homeView(true);
  }

  /** The score screen's legend over the map: which colour is which round. Hidden everywhere else. */
  private legend(show: boolean): void {
    let el = document.getElementById("legend");
    if (!show) {
      el?.remove();
      return;
    }
    if (el) return;
    const item = (key: "pin" | "next" | "who", label: string) => h("li", { "data-route": key }, h("span", { class: `sw ${key}`, "aria-hidden": "true" }), label);
    el = h("ul", { class: "legend", id: "legend", "aria-label": t.summary.legend }, item("pin", t.summary.legendPin), item("next", t.summary.legendNext), item("who", t.summary.legendWho));
    $("mapwrap").append(el);
  }

  private challengeNote(): HTMLElement | null {
    const c = this.challenge();
    if (!c) return null;
    if (this.matchingChallenge()) return h("p", { class: "chal" }, t.challenge.pending(c.day));
    return this.freshChallenge ? h("p", { class: "chal" }, t.challenge.otherDay(c.day, t.levels[c.level].label)) : null;
  }

  private finish(score: number, extra: Omit<RoundResult, "type" | "score">, reveal: string, credits: Record<string, "k" | "l">): void {
    const p = this.p;
    const max = maxOf(p);
    const s = clampRound(score, max);
    this.saved.results[this.round] = { type: p.type, score: s, max, ...extra };
    clearTimeout(this.toastTimer);
    $("toast").hidden = true;
    // Practice (replaying a finished past day): nothing is saved, credited or tracked.
    if (!this.practice) {
      const mode = this.mode();
      const today = localDate();
      this.saved.mode = mode;
      this.saved.playedOn = today;
      store.set(this.key(), this.saved);
      credit(this.p.id, credits, today);
      if (mode === "vault") store.set("vaultLast", today);
      // Pin it: hints used (h); "Whose steps?": wrong guesses (g). Counts only, never what was typed.
      const wrong = extra.tries?.filter((c) => c === "x").length;
      track({ e: "round", l: this.level, d: this.day.number, r: this.round + 1, t: this.p.type, s, x: max, md: mode, ...(extra.clue != null ? { n: extra.clue } : {}), ...(extra.hints != null ? { h: extra.hints } : {}), ...(wrong != null ? { g: wrong } : {}) });
      if (this.saved.results.filter(Boolean).length >= 3) {
        // Only today's puzzle counts for the played streak; the Vault never does.
        const sd = streakDate(mode, today);
        if (sd) recordPlayed(sd);
        const total = dayTotal(this.saved.results);
        const c = this.matchingChallenge();
        track({
          e: "finish",
          l: this.level,
          d: this.day.number,
          s: total,
          x: this.dayMax(),
          n: namedAt(this.saved.results),
          k: streakOf(getPlayed(), today).current,
          md: mode,
          ...(c ? { o: outcome(total, c.score, namedAt(this.saved.results), c.clue) } : {}),
        });
      }
    }
    this.renderHead();
    this.st.done = true;
    this.st.verdict = { score: s, reveal };
    this.draw();
  }

  private verdictBox(): HTMLElement {
    const v = this.st.verdict!;
    const max = maxOf(this.p);
    const word = this.verdictWord(v.score);
    // Band word as a label, unless the round's own verdict already starts with it.
    const label = v.reveal && !v.reveal.toLowerCase().startsWith(word.toLowerCase()) ? h("small", { class: "band" }, word) : null;
    return h(
      "div",
      { class: `verdict v${roundBand(this.p.type, v.score, max)}`, tabindex: "-1" },
      label,
      h("div", { class: "big" }, h("b", null, v.reveal || word), h("span", null, t.ui.points(v.score))),
      this.p.explain ? h("p", null, this.p.explain) : null,
      // The reveal line ("something the clues didn't say") closes the finale, so the score screen needn't repeat round 3.
      this.p.type === "who" && this.p.reveal?.line ? h("p", { class: "fact", "data-act": "reveal-line" }, h("b", null, `${t.ui.didYouKnow} `), this.p.reveal.line) : null,
      h(
        "div",
        { class: "row" },
        h("button", { class: "btn", type: "button", "data-act": "next", onclick: () => this.nextRound() }, this.round < 2 ? t.ui.nextRound : t.ui.seeScore),
      ),
    );
  }

  /** The verdict word for this round's score: "Whose steps?" by place (a win at any place), others by share of the maximum. */
  private verdictWord(score: number): string {
    return this.p.type === "who" ? t.ui.whoVerdict(whoPlaceOf(score)) : t.ui.verdict(per1000(score, maxOf(this.p)));
  }

  private nextRound(): void {
    if (this.introOpen) this.closeIntro(false);
    if (this.quickOpen) this.closeQuick(false);
    this.round++;
    if (this.round >= 3) this.showSummary();
    else this.startRound();
  }

  // ---------- summary ----------
  private showSummary(): void {
    this.round = 3;
    this.stopHub();
    const map = this.map;
    map.onMapClick = null;
    map.onPinClick = null;
    map.setPicking(false);
    map.showCallout(null);
    $("toast").hidden = true;
    const who = this.day.rounds[2];
    this.revealRun++;
    const results = this.saved.results;
    this.dayMap(results);

    const total = dayTotal(results);
    const clue = namedAt(results);
    const titles = this.day.rounds.map((r) => r.title);
    const dayMax = this.dayMax();
    const practice = this.practice;
    const vault = this.mode() === "vault";
    // Challenge links always point at the real site (also from /playtest), so chat previews show the WhoSteps card.
    const url = challengeUrl(location.origin, { level: this.level, day: this.day.number, score: total, clue });
    const text = shareText(this.day.number, this.level, total, dayMax, url, vault);
    const md = this.mode();
    // One share button (icon) that opens three choices: WhatsApp, Telegram (Egor, 30 Sep) and Copy link
    // (desktop, iMessage, Instagram, Slack). No system share sheet: it offers Mail, Notes, Reminders and
    // more, which nobody uses for a daily score.
    const line = shareText(this.day.number, this.level, total, dayMax, undefined, vault);
    const enc = encodeURIComponent;
    const closeMenu = () => {
      menu.hidden = true;
      shareBtn.setAttribute("aria-expanded", "false");
    };
    const copyBtn: HTMLButtonElement = h(
      "button",
      {
        class: "btn ghost",
        type: "button",
        "data-act": "share-copy",
        "data-key": "share-copy",
        onclick: async () => {
          track({ e: "share", l: this.level, d: this.day.number, m: "copy", md });
          try {
            await navigator.clipboard.writeText(text);
            copyBtn.textContent = t.summary.copiedShort;
            this.announce(t.summary.copiedShort);
            window.setTimeout(() => {
              copyBtn.textContent = t.summary.copyLink;
              closeMenu();
            }, 1500);
          } catch {
            // No clipboard (an old browser, an insecure page): select the text for a manual copy.
            const box = h("textarea", { class: "backup-url", readonly: true, rows: 3, "aria-label": t.summary.share }, text);
            menu.after(box);
            box.select();
            this.toast(t.summary.copyFallback);
          }
        },
      },
      t.summary.copyLink,
    );
    // The share image (DECISIONS 2026-10-03 "A spoiler-free share card"): score only, made when the menu opens so
    // a tap can share it at once (iOS needs the share call right after the tap).
    const fileName = `whosteps-${this.day.number}.png`;
    let card: Promise<Blob> | null = null;
    const cardBlob = () => (card ??= renderCard(cardText({ number: this.day.number, level: this.level, vault, results, dayMax })));
    const canShareFiles = (() => {
      try {
        return typeof navigator.canShare === "function" && navigator.canShare({ files: [new File([""], fileName, { type: "image/png" })] });
      } catch {
        return false;
      }
    })();
    const imgBtn: HTMLButtonElement = h(
      "button",
      {
        class: "btn",
        type: "button",
        "data-act": canShareFiles ? "share-image" : "download-image",
        "data-key": "share-image",
        onclick: async () => {
          let blob: Blob;
          try {
            blob = await cardBlob();
          } catch {
            return this.toast(t.card.failed);
          }
          track({ e: "share", l: this.level, d: this.day.number, m: "native", md });
          if (canShareFiles) {
            try {
              await navigator.share({ files: [new File([blob], fileName, { type: "image/png" })], text });
              closeMenu();
            } catch {
              /* the player closed the share sheet */
            }
            return;
          }
          const a = h("a", { href: URL.createObjectURL(blob), download: fileName, hidden: true });
          document.body.append(a);
          a.click();
          a.remove();
          window.setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
          closeMenu();
        },
      },
      canShareFiles ? t.card.shareImage : t.card.downloadImage,
    );
    const menu = h(
      "div",
      { class: "sharemenu", id: "shareMenu", hidden: true, "data-act": "share-menu" },
      imgBtn,
      h("div", { class: "sharerow" }, ...SHARE_APPS.map((app) =>
        h(
          "a",
          {
            class: "btn ghost",
            "data-act": `share-${app}`,
            href: app === "whatsapp" ? `https://wa.me/?text=${enc(text)}` : `https://t.me/share/url?url=${enc(url)}&text=${enc(line)}`,
            target: "_blank",
            rel: "noopener",
            onclick: () => {
              track({ e: "share", l: this.level, d: this.day.number, m: app, md });
              closeMenu();
            },
          },
          t.summary.shareTo[app],
        ),
      ), copyBtn),
    );
    const shareBtn: HTMLButtonElement = h(
      "button",
      {
        class: "btn iconbtn",
        type: "button",
        "data-act": "share",
        "data-text": text,
        "aria-label": t.summary.share,
        "aria-expanded": "false",
        "aria-controls": "shareMenu",
        title: t.summary.share,
        onclick: () => {
          menu.hidden = !menu.hidden;
          shareBtn.setAttribute("aria-expanded", String(!menu.hidden));
          if (!menu.hidden) void cardBlob().catch(() => (card = null));
        },
      },
      shareIcon(),
    );
    // The one main button:
    // - a challenge to a past day, with today's puzzle not done yet: Today's puzzle →
    // - a past day: the next unplayed past day
    // - playtest builds: the next unfinished day in the picker (production has only today)
    let main: HTMLElement | null = null;
    const challenged = !practice && !!this.matchingChallenge();
    if (practice) main = h("button", { class: "btn", type: "button", "data-act": "vault-back", onclick: () => this.openVault(null) }, t.vault.back);
    else if (vault && challenged && this.dailyOf() && !this.dailyDone()) main = h("button", { class: "btn", type: "button", "data-act": "today", onclick: () => this.goToday() }, t.vault.todayArrow);
    else if (vault) {
      const next = nextUnplayed(vaultRows(this.pastRefs(), (id) => this.savedOf(this.level, id)), this.day.number);
      if (next) main = h("button", { class: "btn", type: "button", "data-act": "next-unplayed", onclick: () => this.openPast(next.ref) }, t.vault.nextUnplayed);
    } else if (drafts) {
      // Next: the first unfinished day after this one, wrapping round.
      const unfinished = (i: number) => (store.get<Saved>(this.key(this.days[i]!))?.results.length ?? 0) < 3;
      const order = this.days.map((_, i) => (this.dayIdx + 1 + i) % this.days.length);
      const nextIdx = order.find((i) => i !== this.dayIdx && unfinished(i)) ?? -1;
      if (nextIdx >= 0) main = h("button", { class: "btn", type: "button", "data-act": "next-day", onclick: () => this.loadDay(nextIdx) }, t.summary.next);
    }
    // Today's finished puzzle has no main button: its first "what's next" button (the other level, else Past days)
    // shares the row with the share icon, so both sit side by side on a phone (Egor, 30 Sep).
    const hub = vault || practice ? null : this.hubParts();
    let lead = main;
    if (!main && hub) {
      // …or, when both levels are done and the Vault isn't open yet, the countdown itself.
      lead = hub.other ?? hub.past ?? hub.count;
      if (lead === hub.other) hub.other = null;
      else if (lead === hub.past) hub.past = null;
      else hub.count = null;
    }
    if (lead?.tagName === "BUTTON") lead.classList.add("main");
    if (!main) shareBtn.classList.add("solo");
    const stored = practice ? this.savedOf(this.level, this.day.id) : null;

    const panel = $("panel");
    const before = this.snapshot(panel);
    panel.dataset.rk = "summary";
    panel.dataset.roundType = "summary";
    // The score screen starts with the score: round 3 has already shown the answer, the stops and the fact (Egor, 29 Sep).
    // The map keeps the full route, with notes on the pins; the answer stays on its table row for anyone coming back later.
    const name = h("h2", { class: "score", id: "sumScore", tabindex: "-1" }, n(total), h("small", null, ` / ${n(dayMax)}`));
    panel.replaceChildren(
      ...toNodes([this.introOpen ? this.introCard() : null]),
      this.progressBar(),
      h(
        "div",
        { class: "sum" },
        h("div", { class: "kicker" }, h("span", null, practice ? t.vault.practiceKicker(this.day.number) : vault ? t.vault.summaryKicker(this.day.number, t.levels[this.level].label) : t.summary.kicker(this.day.number, t.levels[this.level].label))),
        name,
        h(
          "table",
          null,
          h("tbody", null, results.map((r, i) => h("tr", null, h("td", null, `${TYPE_ICON[r.type]} ${r.type === "who" ? t.summary.whoRow(titles[i]!, who.answer) : titles[i]}`), h("td", null, `${n(r.score)} / ${n(maxOfResult(r))}${r.hints ? t.pin.hintsRow(r.hints) : ""}`)))),
        ),
        practice && stored ? h("p", { class: "chal", "data-act": "practice-note" }, t.vault.practiceNote(dayTotal(stored.results), stored.results.reduce((a, r) => a + maxOfResult(r), 0))) : null,
        practice ? null : this.challengeResult(total, clue),
        practice ? null : this.streakBox(),
        h("div", { class: "row endrow" }, lead, practice ? null : shareBtn),
        practice ? null : menu,
        vault && !practice
          ? h(
              "div",
              { class: "row links" },
              h("button", { class: "linkbtn", type: "button", "data-act": "vault-back", onclick: () => this.openVault(null) }, t.vault.back),
              h("button", { class: "linkbtn", type: "button", "data-act": "practice", onclick: () => this.startPractice() }, t.vault.playAgain),
            )
          : null,
        hub ? this.hubSection(hub) : null,
        drafts && !practice
          ? h(
              "p",
              { class: "hint" },
              h(
                "button",
                {
                  class: "linkbtn",
                  type: "button",
                  "data-act": "reset-day",
                  onclick: () => {
                    store.del(this.key());
                    this.loadDay(this.dayIdx);
                  },
                },
                t.summary.replay,
              ),
            )
          : null,
        this.backupRow(),
      ),
    );
    panel.scrollTop = 0;
    if (before.inPanel) {
      this.panelToTop(panel);
      name.focus({ preventScroll: true });
    }
    this.renderHead();
  }

  private challengeResult(total: number, clue: number): HTMLElement | null {
    const c = this.matchingChallenge();
    if (!c) return null;
    const o = outcome(total, c.score, clue, c.clue);
    const tb = byTieBreak(total, c.score, clue, c.clue);
    return h(
      "div",
      { class: `chal-res ${o}`, "data-act": "challenge-result" },
      h("div", null, h("span", null, t.challenge.them), h("b", null, t.challenge.line(c.score, c.clue))),
      h("div", null, h("span", null, t.challenge.you), h("b", null, t.challenge.line(total, clue))),
      h("p", null, t.challenge[o], tb ? ` ${t.challenge.tieBreak}` : null),
    );
  }

  private streakBox(): HTMLElement | null {
    const s = streakOf(getPlayed(), localDate());
    if (!s.current) return null;
    return h(
      "div",
      { class: "streak", "data-act": "streak" },
      h("div", null, h("span", null, t.streak.label), h("b", null, t.streak.days(s.current))),
      // Best streak and banked rest days when there are any; no explainer line (Egor, 30 Sep).
      s.best > s.current || s.bank ? h("small", null, [s.best > s.current ? t.streak.best(s.best) : null, s.bank ? t.streak.bank(s.bank) : null].filter(Boolean).join(" · ")) : null,
    );
  }

  private backupRow(): HTMLElement {
    const foot = h("div", { class: "foot" });
    const btn = h(
      "button",
      {
        class: "linkbtn",
        type: "button",
        "data-act": "backup",
        onclick: async () => {
          track({ e: "backup" });
          let link = "";
          try {
            link = `${appUrl()}/#restore=${await encodeBackup(exportState())}`;
            await navigator.clipboard.writeText(link);
            this.toast(t.summary.backupCopied);
          } catch {
            if (!link) return this.toast(t.summary.backupFailed);
            const input = h("input", { class: "backup-url", readonly: true, value: link, "aria-label": t.summary.backup });
            foot.append(input);
            input.select();
            this.toast(t.summary.copyFallback);
          }
        },
      },
      t.summary.backup,
    );
    foot.append(h("span", null, `${t.summary.storageNote} `), btn, h("span", null, " · "), h("a", { href: "/privacy", "data-act": "privacy" }, t.ui.privacy));
    return foot;
  }

  // ---------- after today's puzzle: what's next (no separate home screen) ----------
  /**
   * Under today's finished puzzle: Past days (once the Vault is open), the other level's puzzle if
   * it isn't done yet, and a countdown to the next puzzle.
   */
  private hubParts(): { past: HTMLElement | null; other: HTMLElement | null; count: HTMLElement | null } | null {
    if (!this.isToday()) return null;
    const other = LEVELS.find((l) => l !== this.level)!;
    const otherRef = this.dailyOf(other);
    const showOther = !!otherRef && !this.isDone(other, dayIdOf(otherRef));
    const showPast = this.gate() === "open";
    const later = nextDayDate(index, setFor(this.level), localDate());
    if (!showOther && !showPast && !later) return null;
    const count = later ? h("p", { class: "countdown", "data-act": "countdown" }) : null;
    if (count) this.startCountdown(count, later!, t.hub.nextIn);
    return {
      past: showPast ? h("button", { class: "btn ghost", type: "button", "data-act": "vault-open", onclick: () => this.openVault("summary") }, t.hub.pastDays) : null,
      other: showOther
        ? h(
            "button",
            {
              class: "btn ghost",
              type: "button",
              "data-act": "other-level",
              onclick: () => {
                this.freshChallenge = false;
                this.level = other;
                store.set("level", other);
                this.loadLevel(otherRef!.number);
              },
            },
            t.hub.alsoPlay(t.levels[other].label),
          )
        : null,
      count,
    };
  }

  private hubSection(p: { past: HTMLElement | null; other: HTMLElement | null; count: HTMLElement | null }): HTMLElement | null {
    const buttons = p.past || p.other ? h("div", { class: "row" }, p.past, p.other) : null;
    if (!buttons && !p.count) return null;
    return h("section", { class: "hub", "aria-label": t.hub.label }, buttons, p.count);
  }

  /** Counts down to 06:00 on `next` (when that day's puzzle opens), then offers to play it. */
  private startCountdown(el: HTMLElement, next: string, words: (ms: number) => string): void {
    this.stopHub();
    const tick = () => {
      const ms = msUntilDay(next);
      if (ms <= 0) {
        this.stopHub();
        el.replaceChildren(`${t.hub.ready} `, h("button", { class: "linkbtn", type: "button", "data-act": "play-new", onclick: () => this.goToday() }, t.hub.playNew));
        return;
      }
      el.textContent = words(ms);
    };
    tick();
    this.hubTimer = window.setInterval(tick, 20_000);
  }

  private stopHub(): void {
    clearInterval(this.hubTimer);
    this.hubTimer = 0;
  }

  /** Back to today's puzzle at this level (its summary if it's done). */
  private goToday(): void {
    this.freshChallenge = false;
    const d = this.dailyOf();
    this.loadLevel(d ? d.number : null);
  }

  // ---------- the Vault: past days ----------
  private openVault(from: "summary" | "header" | null): void {
    if (from) track({ e: "vault_open", l: this.level, f: from });
    this.view = "vault";
    this.practice = false;
    this.renderVault(true);
  }

  private renderVault(fresh: boolean): void {
    this.stopHub();
    this.revealRun++;
    const map = this.map;
    map.onMapClick = null;
    map.onPinClick = null;
    map.setPicking(false);
    map.showCallout(null);
    this.legend(false);
    map.set({ pins: [], cities: true });
    map.homeView = (anim) => map.worldView(anim ? 600 : 0);
    if (fresh) map.homeView(true);

    const rows = vaultRows(this.pastRefs(), (id) => this.savedOf(this.level, id));
    const shown = this.unplayedOnly ? rows.filter((r) => r.status !== "done") : rows;
    const panel = $("panel");
    const before = this.snapshot(panel);
    panel.dataset.rk = `vault|${this.level}`;
    panel.dataset.roundType = "vault";
    const title = h("h2", { class: "rt", tabindex: "-1" }, t.vault.title);
    const toggle = h(
      "button",
      {
        class: "chip",
        type: "button",
        "aria-pressed": String(this.unplayedOnly),
        "data-act": "unplayed-only",
        "data-key": "unplayed-only",
        onclick: () => {
          this.unplayedOnly = !this.unplayedOnly;
          store.set("vaultUnplayed", this.unplayedOnly);
          this.renderVault(false);
        },
      },
      t.vault.unplayedOnly,
    );
    const list = shown.length
      ? h(
          "ul",
          { class: "vlist", role: "list" },
          shown.map((r) =>
            h(
              "li",
              null,
              h(
                "button",
                { class: `vday ${r.status}`, type: "button", "data-act": "vault-day", "data-key": `vault-${r.ref.number}`, "data-day": String(r.ref.number), onclick: () => this.openPast(r.ref) },
                h("span", { class: "vn" }, `#${r.ref.number}`),
                h("span", { class: "vd" }, t.vault.date(r.ref.date)),
                h("span", { class: "vs" }, r.status === "done" ? t.vault.score(r.score, r.max) : r.status === "partial" ? t.vault.partial(r.played) : t.vault.unplayed),
              ),
            ),
          ),
        )
      : h("p", { class: "hint" }, t.vault.allPlayed);
    panel.replaceChildren(
      ...toNodes([
        this.introOpen ? this.introCard() : null,
        h("div", { class: "rhead" }, h("div", { class: "kicker" }, h("span", null, t.vault.kicker(t.levels[this.level].label))), title, h("p", { class: "rule" }, t.vault.intro)),
        h("div", { class: "row" }, toggle),
        list,
        h("div", { class: "row" }, h("button", { class: "btn ghost", type: "button", "data-act": "today", onclick: () => this.goToday() }, t.vault.today)),
      ]),
    );
    this.afterRender(panel, before, fresh, title);
    if (fresh) {
      this.panelToTop(panel);
      title.focus({ preventScroll: true });
      this.announce(t.vault.title);
    }
    this.renderHead();
  }

  /** Replay a finished past day for fun: unscored, nothing saved (DECISIONS "The Vault"). */
  private startPractice(): void {
    this.practice = true;
    this.saved = { results: [] };
    this.round = 0;
    this.stopHub();
    this.renderHead();
    this.startRound();
  }

  private comingSoon(): void {
    this.revealRun++;
    this.stopHub();
    $("days").hidden = true;
    this.renderHeadModesOnly();
    const panel = $("panel");
    panel.dataset.roundType = "soon";
    const first = nextDayDate(index, setFor(this.level), localDate());
    const count = first ? h("p", { class: "countdown", "data-act": "countdown" }) : null;
    panel.replaceChildren(h("div", { class: "soon" }, h("p", { class: "motto" }, t.motto), h("p", { class: "clue" }, t.tagline), count ?? h("p", { class: "hint" }, t.ui.comingSoon)));
    if (count) this.startCountdown(count, first!, t.hub.firstIn);
    this.legend(false);
    this.map.set({ pins: [], cities: true });
    this.map.homeView = (anim) => this.map.worldView(anim ? 600 : 0);
    this.map.homeView(false);
  }

  private loadingPanel(msg: string = t.ui.loading): void {
    this.stopHub();
    const panel = $("panel");
    panel.dataset.roundType = "loading";
    panel.replaceChildren(h("div", { class: "soon" }, h("p", { class: "hint" }, msg)));
  }

  private loadFailed(dayNumber: number | null): void {
    track({ e: "load_error", l: this.level });
    this.revealRun++;
    $("days").hidden = true;
    const panel = $("panel");
    panel.dataset.roundType = "error";
    panel.replaceChildren(
      h("div", { class: "soon" },
        h("p", { class: "hint" }, t.ui.loadFailed),
        h("div", { class: "row" }, h("button", { class: "btn", type: "button", "data-act": "retry", onclick: () => this.loadLevel(dayNumber) }, t.ui.retry)),
      ),
    );
  }

  private renderHeadModesOnly(): void {
    this.days = [];
    $("modes").hidden = true;
    $("tot").textContent = "";
  }

  private toast(msg: string): void {
    const el = $("toast");
    el.textContent = msg;
    el.hidden = false;
    clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => (el.hidden = true), Math.max(2600, msg.length * 45));
  }
}
